/**
 * Gesture classification for the device-takeover panel.
 *
 * A deliberate port of `cti-quality-worker/takeover/gesture.js` — the two cannot import each other
 * across repos, so this file must be kept in step with it. Both are unit-tested against the SAME
 * scenarios, including the one that cost a live session: a real user tried to spin a picker wheel and
 * produced seven taps and zero drags, because the browser began a native image drag on the video frame,
 * cancelled the pointer stream, and delivered `pointerup` back at the press point.
 *
 * That is why the far end of a gesture is taken from the movement TRAIL rather than from the up event,
 * and why the panel must also mark the frame un-draggable (`draggable={false}`, `user-select: none`,
 * `touch-action: none`, and a `dragstart` preventDefault) — without those the pointer stream never
 * arrives here at all.
 */

/** Below this distance in device points a gesture is a tap, not a drag. */
export const DRAG_THRESHOLD_POINTS = 8;

export interface DevicePoint {
  x: number;
  y: number;
}

interface FrameSize {
  width: number;
  height: number;
}

interface FrameRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type TakeoverGesture =
  | { kind: 'tap'; x: number; y: number }
  | { kind: 'drag'; x1: number; y1: number; x2: number; y2: number };

/**
 * Convert a click in the DISPLAYED frame into the coordinate space the far side actually uses.
 *
 * Two takeovers, two spaces, one ratio. A device's video frames are physical pixels (e.g. 1170x2532)
 * while its pointer space is logical points (390x844); a browser's frames are a scaled-down picture of a
 * page whose pointer space is its CSS-pixel viewport (e.g. 1600x900). Either way the frame is scaled again
 * to fit the panel, and all of it collapses into this one ratio.
 *
 * `viewport` wins when both arrive: a web takeover carries no device, and a row carrying both would be a
 * contract error, not a choice worth honouring in the direction of the device.
 *
 * When the image is rendered with `object-contain`, `rect` is the outer box and `frame` describes the
 * actual image content inside it. Letterboxed clicks are rejected rather than being scaled into the page.
 *
 * Returns null when neither space is known — the caller must warn instead of substituting the frame's own
 * pixel size, which lands every gesture at an offset with no error anywhere.
 */
export function toDevicePoints(input: {
  clientX: number;
  clientY: number;
  rect: FrameRect | null;
  screen: FrameSize | null | undefined;
  viewport?: FrameSize | null;
  frame?: FrameSize | null;
}): DevicePoint | null {
  const { clientX, clientY, rect, screen, viewport, frame } = input;
  if (!rect || !rect.width || !rect.height) return null;
  const space =
    viewport && viewport.width && viewport.height ? viewport : screen;
  if (!space || !space.width || !space.height) return null;
  const content = containedFrameRect(rect, frame);
  if (!content) return null;
  if (
    clientX < content.left ||
    clientX > content.left + content.width ||
    clientY < content.top ||
    clientY > content.top + content.height
  ) {
    return null;
  }
  const x = Math.round(
    ((clientX - content.left) / content.width) * space.width,
  );
  const y = Math.round(
    ((clientY - content.top) / content.height) * space.height,
  );
  return {
    x: Math.min(space.width - 1, Math.max(0, x)),
    y: Math.min(space.height - 1, Math.max(0, y)),
  };
}

/** Find the actual image content inside an object-contain box. */
function containedFrameRect(
  rect: FrameRect,
  frame: FrameSize | null | undefined,
): FrameRect | null {
  if (!frame) return rect;
  if (
    !Number.isFinite(frame.width) ||
    !Number.isFinite(frame.height) ||
    frame.width <= 0 ||
    frame.height <= 0
  ) {
    return null;
  }
  const frameRatio = frame.width / frame.height;
  const boxRatio = rect.width / rect.height;
  if (frameRatio > boxRatio) {
    const height = rect.width / frameRatio;
    return {
      left: rect.left,
      top: rect.top + (rect.height - height) / 2,
      width: rect.width,
      height,
    };
  }
  const width = rect.height * frameRatio;
  return {
    left: rect.left + (rect.width - width) / 2,
    top: rect.top,
    width,
    height: rect.height,
  };
}

/**
 * Decide whether a pointer interaction was a tap or a drag, and with which endpoints.
 *
 * The far end comes from the furthest point actually OBSERVED, not from the up event, so a drag survives
 * a browser that returns `pointerup` to the press point.
 */
export function classifyGesture(input: {
  from: DevicePoint | null;
  moves?: DevicePoint[];
  to: DevicePoint | null;
  threshold?: number;
}): TakeoverGesture | null {
  const { from, moves = [], to, threshold = DRAG_THRESHOLD_POINTS } = input;
  if (!from) return null;
  const candidates = [...moves, to].filter((p): p is DevicePoint => Boolean(p));
  if (candidates.length === 0) return null;

  let farthest = candidates[0]!;
  let farthestDistance = distance(from, farthest);
  for (const point of candidates) {
    const d = distance(from, point);
    if (d > farthestDistance) {
      farthest = point;
      farthestDistance = d;
    }
  }

  if (farthestDistance <= threshold) {
    // A tap lands where the finger went DOWN, not where it came up: on a live control those differ by a
    // point or two and the press point is what the human aimed at.
    return { kind: 'tap', x: from.x, y: from.y };
  }
  return {
    kind: 'drag',
    x1: from.x,
    y1: from.y,
    x2: farthest.x,
    y2: farthest.y,
  };
}

/** Describe a gesture in the same terms the audit trail records it. */
export function describeGesture(gesture: TakeoverGesture | null): string {
  if (!gesture) return 'nothing to send';
  if (gesture.kind === 'tap') return `tap (${gesture.x},${gesture.y})`;
  return `drag (${gesture.x1},${gesture.y1}) → (${gesture.x2},${gesture.y2})`;
}

/**
 * The goal a human is being asked to reach, rendered from the machine-checkable predicate the WORKER
 * verifies against — so the panel can never advertise a target different from the one that decides the
 * outcome.
 */
export function goalOf(
  verify: {
    kind: string;
    column?: string;
    equals?: string;
    labelContains?: string;
    value?: string;
  } | null,
  observed: string | null,
  options: { observedEffectFallback?: boolean } = {},
): {
  what: string;
  target: string;
  now: string;
  hit: boolean;
  verdict: string;
} {
  if (!verify) {
    if (options.observedEffectFallback) {
      return {
        what: 'observed effect',
        target: 'visible change',
        now: observed ?? '?',
        hit: false,
        verdict:
          'The worker accepts this turn only if your gesture visibly changes the page or device. The next test assertion decides whether the intended action succeeded.',
      };
    }
    return {
      what: '—',
      target: 'no predicate',
      now: observed ?? '?',
      hit: false,
      // An unverifiable takeover ends the case blocked, so say so rather than implying it can pass.
      verdict:
        'without a predicate the result cannot be verified — the case will end blocked',
    };
  }
  const isWheel = verify.kind === 'picker_wheel_value';
  const isTimeField = verify.kind === 'field_value';
  const isText = verify.kind === 'text_contains';
  const isPresence =
    verify.kind === 'element_present' || verify.kind === 'element_absent';
  const isUrl = verify.kind === 'url_matches';

  if (isUrl) {
    return {
      what: 'page URL',
      target: verify.value ?? '?',
      // The worker reports document text as `observed`; it does not report the URL. Showing that text
      // as the current URL would make a successful-looking body contain a false positive.
      now: observed == null ? '?' : 'not exposed in live observation',
      hit: false,
      verdict:
        observed == null
          ? 'waiting for the worker to verify the URL after Submit'
          : 'the live observation does not include the URL — the worker will verify it after Submit',
    };
  }

  if (isTimeField && observed == null) {
    return {
      what: 'time field',
      target: verify.equals ?? '?',
      now: 'not exposed in live observation',
      hit: false,
      verdict: 'Dismiss the picker, then Submit for verification.',
    };
  }

  let target = '?';
  if (isWheel || isTimeField) {
    target = verify.equals ?? '?';
  } else if (isText) {
    target = verify.value ?? '?';
  } else if (verify.kind === 'element_absent') {
    target = 'absent';
  } else if (verify.kind === 'element_present') {
    target = 'present';
  }

  let what = 'unknown predicate';
  if (isWheel) {
    what = `${verify.column ?? '?'} wheel`;
  } else if (isTimeField) {
    what = 'time field';
  } else if (isText) {
    what = 'page text';
  } else if (isPresence) {
    what = verify.labelContains ?? verify.value ?? 'element';
  }
  const now = observed ?? '?';
  const presenceIsKnown =
    !isPresence ||
    observed == null ||
    ['present', 'absent'].includes(now.trim());
  if (isPresence && !presenceIsKnown) {
    return {
      what,
      target,
      // Mobile observations expose a compact present/absent state. Web observations expose page text,
      // which cannot prove whether a selector exists without returning the DOM itself to the panel.
      now: 'not exposed in live observation',
      hit: false,
      verdict:
        'the live observation does not include element presence — the worker will verify it after Submit',
    };
  }
  let hit = false;
  if (observed != null && isText) {
    hit = target !== '?' && observed.includes(target);
  } else if (observed != null && (isWheel || isTimeField || isPresence)) {
    hit = observed.trim() === target.trim();
  }
  let verdict = 'waiting for your first gesture to read the current value';
  if (observed != null && hit) {
    verdict = 'GOAL REACHED — press Submit';
  } else if (observed != null && isText) {
    verdict = `not there yet: page text does not contain ${target}`;
  } else if (observed != null) {
    verdict = `not there yet: ${now} ≠ ${target}`;
  }
  return {
    what,
    target,
    now,
    hit,
    verdict,
  };
}

function distance(a: DevicePoint, b: DevicePoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}
